import {
  ActionIcon,
  AppShell,
  Burger,
  Group,
  NavLink,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  ThemeIcon,
  useMantineColorScheme,
  useComputedColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  IconDashboard,
  IconPackage,
  IconLogout,
  IconChartBar,
  IconTruck,
  IconSettings,
  IconSun,
  IconMoon,
  IconKey,
  IconPhoto,
} from '@tabler/icons-react';
import { useAuth, signOut } from '../lib/auth';

export default function AppShellLayout() {
  const [opened, { toggle }] = useDisclosure();
  const { user } = useAuth();
  const { location } = useRouterState();
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const toggleColorScheme = () => setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark');

  const links = [
    { to: '/', label: 'Dashboard', icon: IconDashboard },
    { to: '/parts', label: 'Parts', icon: IconPackage },
    { to: '/images', label: 'Images', icon: IconPhoto },
    { to: '/shipping', label: 'Shipping', icon: IconTruck },
    { to: '/quality', label: 'Data Quality', icon: IconChartBar },
    { to: '/clients/keys', label: 'Client Keys', icon: IconKey },
  ];

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 230, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header
        style={{
          background:
            'linear-gradient(90deg, rgba(20, 184, 166, 0.10) 0%, rgba(99, 102, 241, 0.07) 100%)',
          borderBottom: '1px solid rgba(20, 184, 166, 0.2)',
        }}
      >
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap={8} align="center">
              <ThemeIcon
                size={32}
                radius="xl"
                variant="gradient"
                gradient={{ from: 'teal', to: 'indigo', deg: 45 }}
              >
                <IconSettings size={20} stroke={2} />
              </ThemeIcon>
              <Text
                fw={300}
                size="xl"
                ff="'Inter', system-ui, sans-serif"
                style={{ letterSpacing: '-0.02em' }}
                variant="gradient"
                gradient={{ from: 'teal', to: 'indigo', deg: 45 }}
              >
                Parts
                <Text span fw={800} inherit>
                  Hub
                </Text>
              </Text>
            </Group>
          </Group>
          <Group gap="sm">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={toggleColorScheme}
              aria-label="Toggle color scheme"
            >
              {computedColorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            <Menu withinPortal position="bottom-end">
              <Menu.Target>
                <UnstyledButton>
                  <Avatar src={user?.picture ?? undefined} radius="xl" size="sm" />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user?.email}</Menu.Label>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => signOut()}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar
        p="md"
        style={{
          background:
            'linear-gradient(180deg, rgba(20, 184, 166, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%)',
        }}
      >
        {links.map((l) => (
          <NavLink
            key={l.to}
            component={Link}
            to={l.to}
            label={l.label}
            leftSection={<l.icon size={18} stroke={1.5} />}
            active={
              location.pathname === l.to || (l.to !== '/' && location.pathname.startsWith(l.to))
            }
            variant="light"
          />
        ))}
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
